import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';

interface GuestPassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  membershipId: string;
}

interface GuestPass {
  id: string;
  guestName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  status: string;
  expiresAt: string | null;
  usedAt: string | null;
  createdAt: string;
}

export default function GuestPassModal({ isOpen, onClose, onSuccess, membershipId }: GuestPassModalProps) {
  const [loading, setLoading] = useState(false);
  const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
  const [formData, setFormData] = useState({
    guestName: '',
    guestPhone: '',
    guestEmail: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchGuestPasses();
    }
  }, [isOpen, membershipId]);

  const fetchGuestPasses = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/member/self-service/guest-passes/${membershipId}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGuestPasses(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching guest passes:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/member/self-service/guest-pass`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          membershipId,
          ...formData
        })
      });

      if (response.ok) {
        toast.success('Guest pass created successfully!');
        setFormData({ guestName: '', guestPhone: '', guestEmail: '' });
        fetchGuestPasses();
        onSuccess();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to create guest pass');
      }
    } catch (error) {
      console.error('Error creating guest pass:', error);
      toast.error('Failed to create guest pass');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">Guest Passes</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Create Guest Pass Form */}
          <form onSubmit={handleSubmit} className="space-y-4 pb-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Create New Guest Pass</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Name
                </label>
                <input
                  type="text"
                  value={formData.guestName}
                  onChange={(e) => setFormData({ ...formData, guestName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Guest name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Phone
                </label>
                <input
                  type="tel"
                  value={formData.guestPhone}
                  onChange={(e) => setFormData({ ...formData, guestPhone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+1234567890"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Guest Email
                </label>
                <input
                  type="email"
                  value={formData.guestEmail}
                  onChange={(e) => setFormData({ ...formData, guestEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="guest@example.com"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Guest Pass'}
            </button>
          </form>

          {/* Guest Passes List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">My Guest Passes</h3>
            {guestPasses.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No guest passes yet</p>
            ) : (
              <div className="space-y-3">
                {guestPasses.map((pass) => (
                  <div key={pass.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{pass.guestName || 'Guest'}</p>
                        {pass.guestPhone && (
                          <p className="text-sm text-gray-600">{pass.guestPhone}</p>
                        )}
                        {pass.guestEmail && (
                          <p className="text-sm text-gray-600">{pass.guestEmail}</p>
                        )}
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${pass.status === 'available'
                          ? 'bg-green-100 text-green-800'
                          : pass.status === 'used'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                        {pass.status}
                      </span>
                    </div>
                    {pass.expiresAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        Expires: {new Date(pass.expiresAt).toLocaleDateString()}
                      </p>
                    )}
                    {pass.usedAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        Used: {new Date(pass.usedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

